// fetch with a timeout, caller-controlled cancellation and an ok-check.

export class FetchError extends Error {
    url: string;
    status: number;

    constructor(url: string, status: number) {
        super(`Request to ${url} failed with status ${status}`);
        this.name = 'FetchError';
        this.url = url;
        this.status = status;
    }
}

export class TimeoutError extends Error {
    constructor(url: string, timeoutMs: number) {
        super(`Request to ${url} timed out after ${timeoutMs} ms`);
        this.name = 'TimeoutError';
    }
}

export interface FetchOptions {
    // Aborting it rejects with an AbortError (see isAbort).
    signal?: AbortSignal;
    timeoutMs?: number;
}

export const DEFAULT_TIMEOUT_MS = 15000;

// True when the request was cancelled by the caller (not a timeout or failure).
export const isAbort = (error: unknown): boolean =>
    error instanceof DOMException && error.name === 'AbortError';

export async function fetchJson(url: string, { signal, timeoutMs = DEFAULT_TIMEOUT_MS }: FetchOptions = {}): Promise<unknown> {
    if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
    }

    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, timeoutMs);
    const onCallerAbort = () => controller.abort();
    signal?.addEventListener('abort', onCallerAbort);

    try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
            throw new FetchError(url, response.status);
        }
        return await response.json();
    } catch (error) {
        if (timedOut) {
            throw new TimeoutError(url, timeoutMs);
        }
        if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }
        throw error;
    } finally {
        clearTimeout(timeoutId);
        signal?.removeEventListener('abort', onCallerAbort);
    }
}
